import React, { useEffect, useRef } from 'react';
import { playWhosOnImageGlitchSound } from '../../../../utils/sounds';
import { getRandomHostAnimation } from './animations';
import ImageWithFallback from '../../../shared/ImageWithFallback';
import { useTranslation } from '../../../../i18n';

const ANIM_MS = 2000;  // Each animation runs for 2 seconds
const BREAK_MS = 1000; // 1 second break (image hidden) between animations

export interface WhosOnImageHostProps {
  imageUrl: string;
  fallbackImageUrl?: string;
  timeLimit: number;
  /** When true, everyone has answered. */
  allAnswered?: boolean;
  /** When true, round ended (time up or everyone answered). Stop animations, show full image. */
  roundEnded?: boolean;
  /** Not used here anymore — correct answer is shown in the title flip in BonusLevelHost. */
  correctAnswer?: string;
  /**
   * When true, image fills its parent (cover during play, contain on reveal).
   * Used on mobile when host is also playing so the portrait uses the full viewport.
   */
  fillViewport?: boolean;
}

/**
 * Host view for "Who's on the image?" bonus game.
 *
 * During the round: the image is animated with random effects (2s animate → 1s break → repeat).
 * A glitch sound plays at the start of each animation cycle.
 *
 * When the round ends: animations stop and the full unblurred image is shown.
 */
export const WhosOnImageHost: React.FC<WhosOnImageHostProps> = ({
  imageUrl,
  fallbackImageUrl,
  timeLimit,
  allAnswered = false,
  roundEnded = false,
  fillViewport = false,
}) => {
  const { t } = useTranslation();
  const imageRef = useRef<HTMLImageElement>(null);
  const animRef = useRef<any>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef(0);

  const showRoundEnd = allAnswered || roundEnded;

  /** Clear all anime.js inline styles from the element */
  const resetStyles = (el: HTMLElement) => {
    el.style.opacity = '0';
    el.style.transform = '';
    el.style.filter = '';
    el.style.transformOrigin = '';
    el.style.clipPath = '';
  };

  /** Stop current animation and clear pending timeouts */
  const stopAnim = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    try { animRef.current?.pause?.(); } catch (_) { /* ignore */ }
    animRef.current = null;
  };

  // ---- Main animation cycle: animate 2s → hide 1s → repeat ----
  // Images are preloaded during intro (BonusLevelHost), so animation can start immediately
  useEffect(() => {
    if (showRoundEnd || !imageRef.current || timeLimit <= 0) return;

    const el = imageRef.current;
    startTimeRef.current = Date.now();
    let cancelled = false;

    // Start hidden
    resetStyles(el);

    const runCycle = () => {
      if (cancelled) return;
      if (Date.now() - startTimeRef.current >= timeLimit) return;

      // Play glitch sound at the start of each animation
      playWhosOnImageGlitchSound();

      // Clean slate for animation (keep opacity 0 — animation will control it)
      el.style.transform = '';
      el.style.filter = '';
      el.style.transformOrigin = '';
      el.style.clipPath = '';

      // Pick a random animation and run it.
      // Each animation goes: opacity 0 → visible peak → 0 over ANIM_MS.
      const animFn = getRandomHostAnimation();
      animRef.current = animFn(el, { duration: ANIM_MS });

      // After ANIM_MS, force-stop animation and start break
      timeoutRef.current = setTimeout(() => {
        if (cancelled) return;

        // Force stop and hide
        try { animRef.current?.pause?.(); } catch (_) { /* ignore */ }
        animRef.current = null;
        resetStyles(el); // opacity → 0, clear transform/filter

        // Break: image stays hidden for BREAK_MS, then next cycle
        timeoutRef.current = setTimeout(() => {
          if (cancelled) return;
          if (Date.now() - startTimeRef.current >= timeLimit) return;
          runCycle();
        }, BREAK_MS);
      }, ANIM_MS);
    };

    runCycle();

    return () => {
      cancelled = true;
      stopAnim();
      if (el) {
        // Restore to fully visible on cleanup
        el.style.opacity = '';
        el.style.transform = '';
        el.style.filter = '';
        el.style.transformOrigin = '';
        el.style.clipPath = '';
      }
    };
  }, [timeLimit, showRoundEnd]);

  // When round ends, stop everything and make image fully visible
  useEffect(() => {
    if (showRoundEnd) {
      stopAnim();
      if (imageRef.current) {
        imageRef.current.style.opacity = '';
        imageRef.current.style.transform = '';
        imageRef.current.style.filter = '';
        imageRef.current.style.transformOrigin = '';
        imageRef.current.style.clipPath = '';
      }
    }
  }, [showRoundEnd]);

  const imgClassName = [
    'whos-on-image-host__img',
    showRoundEnd ? 'whos-on-image-host__img--reveal' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className="whos-on-image-host"
      style={
        fillViewport
          ? undefined
          : {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flex: '1 1 auto',
              minHeight: 200,
              padding: '0.5rem',
              width: '100%',
              overflow: 'visible',
              perspective: 1200,
              transformStyle: 'preserve-3d',
            }
      }
    >
      <ImageWithFallback
        ref={imageRef}
        src={imageUrl}
        fallbackSrc={fallbackImageUrl}
        alt={t('gameplay.imageAlt.whoOrWhat')}
        loading="eager"
        className={fillViewport ? imgClassName : undefined}
        style={
          fillViewport
            ? {
                // Size/fit via CSS; keep transform-friendly defaults for anime.js
                perspective: 1200,
                transformStyle: 'preserve-3d',
                ...(showRoundEnd ? { animation: 'fadeIn 0.5s ease-out' } : {}),
              }
            : {
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                maxHeight: showRoundEnd ? 'min(320px, 35vh)' : 'min(520px, 65vh)',
                objectFit: 'contain',
                borderRadius: 12,
                ...(showRoundEnd ? { animation: 'fadeIn 0.5s ease-out' } : {}),
              }
        }
      />
    </div>
  );
};
